import parse from 'html-react-parser';
import { decodeHtmlPayload } from '@akademi/site-builder-runtime';
import { tx } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import { type PaddingBox } from '../_shared/resolvePadding';

/**
 * Renders operator-authored HTML. The admin panel edits the
 * {@code html} field as a plain multiline editor (no WYSIWYG), and this
 * block renders it.
 *
 * <h3>How it renders</h3>
 * Mirrors {@code akademi-lms-v2}'s static-page rendering
 * ({@code pages/public/[detail].js} → {@code utils/htmlParser}): the
 * markup string is run through a parser
 * ({@code html-react-parser}'s {@code parse}, the maintained successor
 * of v2's {@code react-html-parser}) that turns it into real React
 * elements, instead of injecting a raw string with
 * {@code dangerouslySetInnerHTML}. This keeps rendering SSR-safe (no
 * DOM required, no hydration hacks) and lets embedded {@code <iframe>}
 * video embeds, images, and links come through as proper nodes —
 * exactly what the v2 {@code .public-page-body} region did.
 *
 * <p>Content is operator-authored (trusted); the editor is the trust
 * boundary. The parser drops nothing by default beyond what's invalid
 * HTML.
 *
 * <h3>Width</h3>
 * <ul>
 *   <li>{@code maxWidth='full'} (default) — no clamp, bare container;
 *       markup controls its own width.</li>
 *   <li>{@code 'narrow' | 'default' | 'wide'} — center the markup in a
 *       {@link SectionShell} with a max-width clamp for prose-style
 *       snippets.</li>
 * </ul>
 *
 * <p>Sibling to {@code RichText} — RichText wraps WYSIWYG output in a
 * themed surface; HtmlBlock is the deliberately un-themed raw-paste
 * primitive.
 */
const widthMap: Record<string, number> = {
  narrow: 640,
  default: 760,
  wide: 960,
};

export interface HtmlBlockProps {
  /** Raw HTML markup; parsed into React elements before render. */
  html?: unknown;
  /** Width behaviour. 'full' = no clamp; others center + clamp. */
  maxWidth?: 'full' | keyof typeof widthMap;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

export function HtmlBlock({ html, maxWidth = 'full', paddingOuter, paddingInner }: HtmlBlockProps) {
  // Stored value is base64-encoded (backend strips raw HTML tags on save,
  // so the admin persists it as `b64:…`); decode back to raw markup first.
  // Plain/legacy values pass through unchanged.
  const markup = decodeHtmlPayload(tx(html));
  // Same rendering path as akademi-lms-v2's htmlParser: parse the HTML
  // string into React elements (SSR-safe), not dangerouslySetInnerHTML.
  const content = parse(markup);

  // Full-bleed: bare container, no SectionShell, no width clamp, no
  // themed-typography class. Markup controls its own layout.
  if (maxWidth === 'full') {
    return <div className="lms-html-block">{content}</div>;
  }

  const width = widthMap[maxWidth] ?? widthMap['default']!;

  return (
    <SectionShell paddingOuter={paddingOuter} paddingInner={paddingInner}>
      <div className="lms-html-block" style={{ maxWidth: width, margin: '0 auto' }}>
        {content}
      </div>
    </SectionShell>
  );
}
